import { AccessoriesItemData } from '../../accessories/data/accessory-assets.class';
import { COUNTERTOP_MOK_KEY_PREFIX, COUNTERTOP_NONE_KEY } from '../../countertop-material/constants';
import { CountertopSkuAssetData } from '../../countertops/data/countertop-sku-asset-data.class';
import { getBacksplashLineItemCfn } from './get-backsplash-line-item.cfn';
import { Island } from '../../islands/island.class';
import { IslandSkuAssetData } from '../../islands/data/island-sku-asset-data.class';
import { Kitchen } from '../kitchen.class';
import { isTopModelSku } from '../../../component-classes/top-model-extensions';
import { KitchenItemizedMsrp } from '../types/kitchen-itemized-msrp.type';
import { totalAdjustmentCfn } from '../../promotional-skus/functions';

// Read through a cast until the catalog DataShapes type `discontinued`; an untyped catalog reads as not discontinued
const isDiscontinued = (record: unknown): boolean => (record as { discontinued?: boolean } | null)?.discontinued === true;

// A bare structural CUTOUT is internal noise, but a hand-placed override (same generic sku, real typed-in label) belongs on the quote (GitHub #513)
const isStructuralNoiseSku = (item: { sku: string; isCustomOverride?: boolean }): boolean =>
	item.sku.includes('VENT') || item.sku.includes('PASSTHROUGH') || isTopModelSku(item.sku) || (item.sku.includes('CUTOUT') && !item.isCustomOverride);

/**
 * Builds the full kitchen pricing breakdown with items ordered by natural spatial position.
 * Uses orderedChildren to traverse islands left-to-right by connection chain, with galley
 * islands last. Items within each island follow panel order, groups left-to-right, items
 * top-to-bottom. Duplicate SKUs are merged into their first spatial occurrence.
 * @param kitchen Kitchen to calculate the itemized MSRP for
 * @returns KitchenItemizedMsrp
 */
export const getItemizedMsrpCfn = (kitchen: Kitchen): KitchenItemizedMsrp => {
	const islands = kitchen.design.islands.orderedChildren;

	const kitchenItemized: KitchenItemizedMsrp = {
		subtotal: 0,
		subtotalWithoutDiscount: 0,
		tax: kitchen.tax,
		shippingAmount: 0,
		isFreeShipping: false,
		total: 0,
		countertops: [],
		islands: [],
		items: [],
		accessories: [],
		backsplashes: []
	};

	// const subtotal = islands.reduce((acc, island) => acc + island.msrpItemized.island, 0);

	islands.forEach((singleIsland: Island) => {
		const { countertop, items } = singleIsland.msrpItemized;

		// countertops msrp - exclude non-countertop and MOKS materials; an island with no countertop has nothing to price
		const islandCountertop = singleIsland.getCountertop();
		const materialKey = islandCountertop?.material?.key ?? COUNTERTOP_NONE_KEY;
		const isExcludedCountertop = materialKey === COUNTERTOP_NONE_KEY || materialKey.startsWith(COUNTERTOP_MOK_KEY_PREFIX);

		if (!isExcludedCountertop) {
			kitchenItemized.subtotal += countertop.total;
			const existingCountertop = kitchenItemized.countertops.find((ctItem) => ctItem.sku === countertop.sku);

			if (existingCountertop) {
				existingCountertop.qty += 1;
				existingCountertop.total += countertop.total;
			} else {
				const countertopData = CountertopSkuAssetData.findBySku(countertop.sku);
				kitchenItemized.countertops.push({
					qty: 1,
					sku: countertop.sku,
					hsProductId: countertopData?.hsProductId ?? null,
					category: countertop.category,
					description: countertop.description,
					each: countertop.each,
					total: countertop.total,
					discontinued: isDiscontinued(countertopData)
				});
			}
		}

		// island base msrp
		kitchenItemized.subtotal += singleIsland.msrpBase;

		const existingIsland = kitchenItemized.islands.find((islandItem) => (islandItem.sku === singleIsland.sku) &&
			(islandItem.description.substring(islandItem.description.indexOf(' ') + 1)[0] === singleIsland.description.substring(singleIsland.description.indexOf(' ') + 1)[0]));

		if (existingIsland) {
			existingIsland.qty += 1;
			existingIsland.total += singleIsland.msrpBase;
		} else {
			const islandData = IslandSkuAssetData.findBySku(singleIsland.sku);
			kitchenItemized.islands.push({
				qty: 1,
				sku: singleIsland.sku,
				hsProductId: islandData?.hsProductId ?? null,
				category: 'island',
				description: singleIsland.description,
				each: singleIsland.msrpBase,
				total: singleIsland.msrpBase,
				discontinued: isDiscontinued(islandData)
			});
		}

		// items: appliances, cutouts, etc
		items.forEach((item) => {
			kitchenItemized.subtotal += item.total;

			// check if kitchenIslandItems already has the item
			const existingItem = kitchenItemized.items.find((kitchenItem) => kitchenItem.sku === item.sku && (!item.isCustomOverride || kitchenItem.description === item.description));

			if (!isStructuralNoiseSku(item)) {
				if (existingItem) {
					existingItem.qty += item.qty;
					existingItem.total += item.total;
				} else {
					kitchenItemized.items.push(item);
				}
			}
		});
	});

	// accessories - add BEFORE promotions
	kitchen.accessories.children.forEach((accessory) => {
		kitchenItemized.subtotal += accessory.msrp;

		const existingAccessory = kitchenItemized.accessories.find((a) => a.sku === accessory.sku);

		if (existingAccessory) {
			existingAccessory.qty++;
			existingAccessory.total += accessory.msrp;
		} else {
			kitchenItemized.accessories.push({
				qty: 1,
				sku: accessory.sku,
				hsProductId: accessory.hsProductId ?? null,
				category: 'accessory',
				description: accessory.description,
				each: accessory.msrp,
				total: accessory.msrp,
				discontinued: isDiscontinued(AccessoriesItemData.findBySku(accessory.sku))
			});
		}
	});

	// backsplashes - priced per-material when resolvable, generic CUSTOM_BACKSPLASH otherwise
	const backsplashLineItem = getBacksplashLineItemCfn(kitchen);
	if (backsplashLineItem) {
		const { sku, hsProductId, description, total, warning } = backsplashLineItem;
		kitchenItemized.subtotal += total;
		kitchenItemized.backsplashes.push({
			qty: 1,
			sku,
			hsProductId,
			category: 'backsplash',
			description,
			each: total,
			total,
			warning
		});
	}

	// Islands and items preserve natural spatial order from orderedChildren traversal.
	// Duplicate SKUs are merged into their first spatial occurrence.

	// Calculate shipping BEFORE promotions are applied
	// Save the pre-promotion subtotal for shipping calculation
	const subtotalBeforePromotions = kitchenItemized.subtotal;

	const isFreeShipping = kitchen.promotionalSkus.children.some((p) => p.sku === 'FREE-SHIPPING');

	kitchen.promotionalSkus.children
		.filter((p) => p.sku === 'SHIPPING' || p.sku === 'FREE-SHIPPING')
		.forEach((shipping) => {
			shipping.totalAdjustment = subtotalBeforePromotions * (shipping.priceAdjustment / 100);
		});

	const shippingSku = kitchen.promotionalSkus.children.find((p) => p.sku === 'SHIPPING');
	kitchenItemized.shippingAmount = shippingSku?.totalAdjustment ?? 0;
	kitchenItemized.isFreeShipping = isFreeShipping;

	// Apply ISLAND / ISLAND_BASE promotions AFTER shipping is calculated
	// ISLAND = island base + countertop combined, ISLAND_BASE = island base only
	const islandBaseTotal = kitchenItemized.islands.reduce((prev, curr) => prev + curr.total, 0);
	const countertopTotal = kitchenItemized.countertops.reduce((prev, curr) => prev + curr.total, 0);
	const islandTotal = islandBaseTotal + countertopTotal;
	const islandTargets = new Set(['ISLAND', 'ISLAND_BASE']);

	kitchen.promotionalSkus.children.forEach((promotion) => {
		const { hasDiscountRules, discountRules, requiredSkus, priceAdjustment } = promotion;

		if (hasDiscountRules) {
			// discountRules path: process ISLAND and ISLAND_BASE rules here
			let islandDiscount = 0;
			const breakdown: Record<string, number> = {};
			// Same rule as totalAdjustmentCfn: the sku-level amount wins when there is exactly one rule.
			const singleRuleAmount = discountRules.length === 1 ? priceAdjustment : null;

			discountRules.forEach((rule) => {
				const { target, adjustmentType: ruleType } = rule;
				const ruleAdjustment = singleRuleAmount ?? rule.priceAdjustment;
				if (islandTargets.has(target)) {
					const base = target === 'ISLAND_BASE' ? islandBaseTotal : islandTotal;
					const amount = ruleType === 'PERCENTAGE'
						? (ruleAdjustment / 100) * base
						: ruleAdjustment;
					islandDiscount += amount;
					breakdown[target] = (breakdown[target] ?? 0) + amount;
				}
			});

			if (islandDiscount !== 0) {
				// Initialize ruleBreakdown with island portion (non-island added in totalAdjustmentCfn)
				promotion.ruleBreakdown = breakdown;

				// Only set totalAdjustment here if ALL rules are island-scoped;
				// otherwise totalAdjustmentCfn will add the non-island portion
				const hasNonIslandRules = discountRules.some((r) => !islandTargets.has(r.target));
				if (!hasNonIslandRules) {
					promotion.totalAdjustment = islandDiscount;
				}
				kitchenItemized.subtotal += islandDiscount;
			}
		} else {
			// Legacy path: single priceAdjustment
			if (requiredSkus && (requiredSkus.includes('ISLAND') || requiredSkus.includes('ISLAND_BASE'))) {
				const base = requiredSkus.includes('ISLAND_BASE') ? islandBaseTotal : islandTotal;
				const singleIslandDiscount = (priceAdjustment / 100) * base;
				promotion.totalAdjustment = singleIslandDiscount;
				kitchenItemized.subtotal += singleIslandDiscount;
			}
		}
	});

	kitchen.promotionalSkus.sortChildren();

	kitchenItemized.subtotalWithoutDiscount = kitchenItemized.subtotal + (isFreeShipping ? 0 : kitchenItemized.shippingAmount);

	// Phase 1: Apply discount-type SKUs first (e.g., NON-COUNTERTOP-SALE)
	// These reduce the subtotal before promotions are calculated
	let discountAdjustment = 0;
	kitchen.promotionalSkus.children
		.filter((p) => p.isDiscount)
		.forEach((p) => {
			discountAdjustment += totalAdjustmentCfn(kitchenItemized.subtotalWithoutDiscount, p);
		});

	// Phase 2: Apply promotion-type SKUs against the discount-adjusted subtotal
	const subtotalAfterDiscounts = kitchenItemized.subtotalWithoutDiscount + discountAdjustment;
	let promotionAdjustment = 0;
	kitchen.promotionalSkus.children
		.filter((p) => !p.isDiscount && !p.isFinal)
		.forEach((p) => {
			promotionAdjustment += totalAdjustmentCfn(subtotalAfterDiscounts, p);
		});

	kitchenItemized.subtotal = subtotalAfterDiscounts + promotionAdjustment;

	// Phase 3: Final adjustments against the post-promotion subtotal
	let finalAdjustment = 0;
	kitchen.promotionalSkus.children
		.filter((p) => p.isFinal)
		.forEach((p) => {
			finalAdjustment += totalAdjustmentCfn(kitchenItemized.subtotal, p);
		});

	kitchenItemized.subtotal += finalAdjustment;

	kitchenItemized.total = kitchenItemized.subtotal + kitchenItemized.tax;

	return kitchenItemized;
};
